import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { SpinnerCircular } from "spinners-react";
import styled, { useTheme } from "styled-components";
import { bookAdminApi } from "../../../app-api/books";
import { Body, Title2, Title3 } from "../../../styles/typography";
import Navbar from "../../book/components/Navbar";
import { useNavigate } from "react-router-dom";

// Styled components
const OrdersContainer = styled.div`
  margin: 0 auto;
  margin-top: 98px;
  padding: 24px;
  background: rgb(${(props) => props.theme.colors.Bgprimary});
  min-height: calc(100vh - 114px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1000px;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  background: rgb(${(props) => props.theme.colors.White});
  box-shadow: 0 2px 8px rgba(${(props) => props.theme.colors.neutral400}, 0.05);
  border-radius: 8px;
  table-layout: fixed;
  padding: 8px;
  border-collapse: collapse;
  position: relative;
  thead,
  th {
    position: sticky;
    top: 0;
  }
`;

const TableHead = styled.thead`
  background: rgb(${(props) => props.theme.colors.white});
`;

const TableHeader = styled(Title3)`
  padding: 1rem;
  text-align: left;
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-weight: 600;
  font-size: 1rem;
  border-bottom: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  &:first-child {
    width: 5%;
  }
  &:nth-child(2) {
    width: 20%;
  }
  &:nth-child(3) {
    width: 20%;
  }
  &:nth-child(4) {
    width: 15%;
  }
  &:nth-child(5) {
    width: 20%;
  }
  &:nth-child(6) {
    width: 20%;
  }
`;

const ScrollableTableContainer = styled.div`
  max-height: 600px;
  overflow: auto;
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  border-radius: 8px;
  padding: 8px;
  padding-top: 0;
  .infinite-scroll-component {
    overflow: unset !important;
  }
`;

const TableRow = styled.tr`
  transition: background 200ms ease-in-out;
  background: rgba(${(props) => props.theme.colors.white});

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      box-shadow: ${(p) => p.theme.shadows.shadow200()};
    }
  }

  &:nth-child(even) {
    background-color: rgb(${(p) => p.theme.colors.offWhite});
  }
`;

const TableCell = styled(Body)`
  padding: 1rem;
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-size: 1rem;
  border-bottom: 1px solid rgb(${(props) => props.theme.colors.neutral150});
  &:first-child {
    width: 5%;
  }
  &:nth-child(2) {
    width: 20%;
  }
  &:nth-child(3) {
    width: 20%;
  }
  &:nth-child(4) {
    width: 15%;
  }
  &:nth-child(5) {
    width: 20%;
  }
  &:nth-child(6) {
    width: 20%;
  }
`;

const Orders = () => {
  const theme = useTheme();
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const abortController = new AbortController();
    bookAdminApi
      .getSubmission(pageIndex, abortController.signal)
      .then((res) => {
        setHasMore(res.page.currentPage < res.page.totalPages - 1);
        setOrders((prev) => prev.concat(res.data));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort("");
    };
  }, [pageIndex]);

  if (loading) {
    return (
      <SpinnerCircular
        size={48}
        thickness={200}
        speed={100}
        color={`rgba(${theme.colors.neutral200}, 1)`}
        secondaryColor={`rgba(${theme.colors.neutral100}, 1)`}
        style={{
          justifySelf: "center",
          position: "relative",
          left: "45%",
        }}
      />
    );
  }

  const LATEST_BOOK_APP_FLOW_DATE = DateTime.fromFormat(
    "2026-08-06 16:40:00",
    "yyyy-MM-dd HH:mm:ss",
    { zone: "America/Los_Angeles" },
  );

  const parseUpdatedAt = (updatedAt) =>
    DateTime.fromFormat(updatedAt, "yyyy-MM-dd HH:mm:ss", {
      zone: "UTC",
    }).toLocal();

  const getOrderStatus = (order) => {
    if (order.addOns !== null) return "new";
    const updatedAt = parseUpdatedAt(order.updatedAt);
    return updatedAt < LATEST_BOOK_APP_FLOW_DATE ? "old" : "draft";
  };

  const STATUS_CONFIG = {
    draft: { label: "Draft", colorKey: "error400", bgKey: "error100" },
    old: {
      label: "Submitted using old flow",
      colorKey: "pineapple400",
      bgKey: "pineapple100",
    },
    new: {
      label: "Submitted using new flow",
      colorKey: "accent400",
      bgKey: "accent100",
    },
  };

  return (
    <>
      <Navbar />
      <OrdersContainer>
        <Title2 as="h2">Book Orders</Title2>
        <ScrollableTableContainer id="scrollableTable">
          <InfiniteScroll
            dataLength={orders.length}
            next={() => {
              setPageIndex((prev) => prev + 1);
            }}
            hasMore={hasMore}
            scrollThreshold={0.5}
            scrollableTarget="scrollableTable"
            loader={
              <SpinnerCircular
                size={48}
                thickness={200}
                speed={100}
                color={`rgba(${theme.colors.neutral200}, 1)`}
                secondaryColor={`rgba(${theme.colors.neutral100}, 1)`}
                style={{
                  justifySelf: "center",
                  position: "relative",
                  left: "45%",
                }}
              />
            }
          >
            <Table>
              <TableHead>
                <tr>
                  <TableHeader as="th">S.No</TableHeader>
                  <TableHeader as="th">F. Name</TableHeader>
                  <TableHeader as="th">L. Name</TableHeader>
                  <TableHeader as="th"># chapters</TableHeader>
                  <TableHeader as="th">Updated At</TableHeader>
                  <TableHeader as="th">Status</TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {orders.map((order, idx) => {
                  const status = getOrderStatus(order);
                  const { label, colorKey, bgKey } = STATUS_CONFIG[status];

                  return (
                    <TableRow
                      onClick={() =>
                        navigate(
                          `${order.bookID}?name=${order.user?.name?.first} ${order.user?.name?.last}&total=${order.memoryCount}`,
                          { state: { order } },
                        )
                      }
                      key={order.bookID}
                    >
                      <TableCell as="td">{idx + 1}</TableCell>
                      <TableCell as="td">{order.user?.name?.first}</TableCell>
                      <TableCell as="td">{order.user?.name?.last}</TableCell>
                      <TableCell as="td">{order.memoryCount}</TableCell>
                      <TableCell as="td">
                        {parseUpdatedAt(order.updatedAt).toFormat(
                          "MMM dd, yyyy hh:mm a",
                        )}
                      </TableCell>
                      <TableCell as="td">
                        <div
                          style={{
                            borderRadius: "8px",
                            padding: "4px 8px",
                            background: `rgb(${theme.colors[bgKey]})`,
                            color: `rgb(${theme.colors[colorKey]})`,
                            border: `1px solid rgb(${theme.colors[colorKey]})`,
                            width: "fit-content",
                          }}
                        >
                          {label}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </InfiniteScroll>
        </ScrollableTableContainer>
      </OrdersContainer>
    </>
  );
};

export default Orders;
